import { GetRecipeDocument, TagType, type GetRecipeQuery, type GetRecipeQueryVariables, type Recipe, type RecipeIngredient, type RecipeStep } from "src/graphql/generated/operations";
import {
    RecipeIngredientsSection,
    RecipeInstructionsSection,
    type RecipeDetailIngredientItem,
    type RecipeDetailStep,
} from "@mealmanager/recipe-detail-ui";
import { useQuery } from "@apollo/client/react";
import { useNavigate, useRouteLoaderData } from "react-router";
import Loading from "~/components/Loading";
import { FormatIngredientDisplayName, FormatQuantityAndUnit, formatDisplayedTagName, normalizeRecipeCategory } from "src/graphql/utils";
import { RecipeImageFlex } from "~/components/graphics/RecipeImage";
import NotFound from "~/components/NotFound";
import { Card, Row, Col, Typography, Tag, Tabs, Space, Button, Grid, theme } from "antd";
import MacrosTable from "~/components/graphics/MacrosTable";
import { ArrowLeftOutlined, ClockCircleOutlined, LinkOutlined } from "@ant-design/icons";
import RatingIndicator from "~/components/graphics/RatingIndicator";
import PagePadding from "~/components/PagePadding";
import { sageCreamPalette } from "~/components/AppShell/Theme";
import { formatRecipeDuration, formatRecipeTotalTime } from "@mealmanager/recipe-card-ui";
import { PiChefHatLight } from "react-icons/pi";
import { useMemo, type ReactNode } from "react";

type RecipesProps = {
    recid: string;
};

type RecipeDetailIngredientItemWithPurchase = RecipeDetailIngredientItem & {
    ingredientQuantity?: number | null;
    ingredientUnit?: string | null;
    ingredientUnitName?: string | null;
};

const { Title, Text, Paragraph } = Typography;

const getTotalDuration = formatRecipeTotalTime;

function getSkillLabel(recipe: Pick<Recipe, "skillsRequirements">): string {
    const skill = recipe.skillsRequirements ?? 0;
    if (skill === 1) return "Trivial";
    if (skill === 2) return "Easy";
    if (skill === 3) return "Complex";
    if (skill === 4) return "Expert";
    return "";
}

function buildRecipeDetailTags(recipe: Recipe): ReactNode[] {
    const tags: ReactNode[] = [];
    const skillLabel = getSkillLabel(recipe);
    const detailTagStyle = {
        margin: 0,
        border: "1px solid #E0DED4",
        background: "#F0F4ED",
        color: "#31583A",
    };

    recipe.tags?.filter((tag) => tag.tag.type === TagType.Auto).forEach((tag) => {
        tags.push(
            <Tag key={`keto-${tag.tag.id ?? tag.tag.name}`} style={detailTagStyle}>
                {formatDisplayedTagName(tag.tag.name)}
            </Tag>
        );
    });

    tags.push(
        <Tag key="type" style={detailTagStyle}>
            {recipe.type}
        </Tag>
    );

    tags.push(
        <Tag key="category" style={detailTagStyle}>
            {normalizeRecipeCategory(recipe.category)}
        </Tag>
    );

    recipe.tags?.filter((tag) => tag.tag.type === TagType.Cuisine).forEach((tag) => {
        tags.push(
            <Tag
                icon={<span className="anticon"><PiChefHatLight /></span>}
                key={`cuisine-${tag.tag.id ?? tag.tag.name}`}
                style={detailTagStyle}
            >
                Cuisine: {formatDisplayedTagName(tag.tag.name)}
            </Tag>
        );
    });

    const prepTimeLabel = formatRecipeDuration(recipe.preparationTimeMin);
    if (prepTimeLabel) {
        tags.push(
            <Tag icon={<ClockCircleOutlined />} key="prep-time" style={detailTagStyle}>
                Prep: {prepTimeLabel}
            </Tag>
        );
    }

    const cookTimeLabel = formatRecipeDuration(recipe.cookingTimeMin);
    if (cookTimeLabel) {
        tags.push(
            <Tag icon={<ClockCircleOutlined />} key="cook-time" style={detailTagStyle}>
                Cook: {cookTimeLabel}
            </Tag>
        );
    }

    if (skillLabel) {
        tags.push(
            <Tag key="skills" style={detailTagStyle}>
                Skills: {skillLabel}
            </Tag>
        );
    }

    return tags;
}

export default function Page({ recid }: RecipesProps) {
    const navigate = useNavigate();
    const routeData = useRouteLoaderData('routes/recipe') as { recipe?: Recipe | null } | undefined;
    const { token } = theme.useToken();
    const screens = Grid.useBreakpoint();
    const isLg = !!screens.lg;

    const { loading, data } = useQuery<GetRecipeQuery, GetRecipeQueryVariables>(GetRecipeDocument, {
        variables: { id: recid ? recid : "" },
        fetchPolicy: routeData?.recipe ? "cache-and-network" : "network-only",
    });

    const hydratedRecipe = data?.recipe ?? routeData?.recipe ?? null;
    const isLoading = loading && !hydratedRecipe;
    const recipeDetailTags = useMemo(() => (hydratedRecipe ? buildRecipeDetailTags(hydratedRecipe) : []), [hydratedRecipe]);
    const instructionSteps: RecipeDetailStep[] = useMemo(() => (hydratedRecipe?.steps as RecipeStep[] | undefined)?.slice().sort((a, b) => a.stepNumber - b.stepNumber).map((step, index) => ({
        id: step.id ?? `${index}-${step.description}`,
        stepNumber: step.stepNumber,
        description: step.description ?? "",
        ingredients: step.ingredients?.map((ingredient, ingredientIndex) => ({
            id: `${index}-${ingredientIndex}-${ingredient.ingredient.id ?? ingredient.ingredient.name}`,
            name: FormatIngredientDisplayName(ingredient.ingredient.name ?? "", ingredient.unit, ingredient.ingredient.unitName ?? undefined, ingredient.quantity),
            quantity: ingredient.quantity,
            unit: ingredient.unit,
            unitName: ingredient.ingredient.unitName,
            ingredientQuantity: ingredient.ingredientQuantity,
            ingredientUnit: ingredient.ingredient.unit,
            ingredientUnitName: ingredient.ingredient.unitName,
        })),
    })) ?? [], [hydratedRecipe]);
    const consolidatedIngredients: RecipeDetailIngredientItemWithPurchase[] = useMemo(() => (hydratedRecipe?.consolidatedIngredients as RecipeIngredient[] | undefined)?.map((item, index) => ({
        id: item.id ?? `${index}-${item.ingredient.id ?? item.ingredient.name}`,
        name: FormatIngredientDisplayName(item.ingredient.name ?? "", item.unit, item.ingredient.unitName ?? undefined, item.quantity),
        quantity: item.quantity,
        unit: item.unit,
        unitName: item.ingredient.unitName,
        ingredientQuantity: item.ingredientQuantity,
        ingredientUnit: item.ingredient.unit,
        ingredientUnitName: item.ingredient.unitName,
    })) ?? [], [hydratedRecipe]);

    if (isLoading) {
        return (
            <PagePadding>
                <Loading />
            </PagePadding>
        );
    }

    if (!hydratedRecipe) {
        return (
            <PagePadding>
                <NotFound />
            </PagePadding>
        );
    }

    const totalDuration = getTotalDuration(hydratedRecipe);
    const formatQuantity = (item: RecipeDetailIngredientItemWithPurchase) => {
        const primary = FormatQuantityAndUnit(item.quantity, item.unit, 2, item.unitName || undefined, item.name);
        if (item.ingredientQuantity == null || !item.ingredientUnit || item.ingredientUnit === item.unit) {
            return primary;
        }
        const secondary = FormatQuantityAndUnit(item.ingredientQuantity, item.ingredientUnit, 2, item.ingredientUnitName || undefined, item.name, true);
        return secondary !== primary ? `${primary} (${secondary})` : primary;
    };

    return (
        <PagePadding style={{ background: sageCreamPalette.pageBg, gap: isLg ? 22 : 14 }}>
            <section
                style={{
                    position: "relative",
                    borderRadius: isLg ? 28 : 22,
                    overflow: "hidden",
                    border: `1px solid ${sageCreamPalette.border}`,
                    background: sageCreamPalette.cardBg,
                    boxShadow: "0 12px 30px rgba(49,55,43,0.08)",
                }}
            >
                <div style={{ height: isLg ? 440 : 310, background: sageCreamPalette.sagePale }}>
                    <RecipeImageFlex recipe={hydratedRecipe} variant="hero" />
                </div>
                <Button
                    shape="circle"
                    aria-label="Back"
                    onClick={() => navigate(-1)}
                    icon={<ArrowLeftOutlined />}
                    style={{
                        position: "absolute",
                        zIndex: 2,
                        top: 16,
                        left: 16,
                        width: 42,
                        height: 42,
                        background: "rgba(255,254,250,0.9)",
                    }}
                />
                <div
                    style={{
                        padding: isLg ? "34px 38px 36px" : "24px 20px 26px",
                        background: sageCreamPalette.cardBg,
                    }}
                >
                    <Row gutter={[24, 20]} align="bottom">
                        <Col xs={24} lg={15}>
                            <Title className="display-heading app-page-title" level={1} style={{ margin: 0, fontSize: isLg ? 40 : 30, lineHeight: 1.08 }}>
                                {hydratedRecipe.name}
                            </Title>
                            <Space wrap size={[12, 8]} style={{ marginTop: 14 }}>
                                <RatingIndicator recipe={hydratedRecipe} />
                                {totalDuration ? (
                                    <Text type="secondary"><ClockCircleOutlined /> {totalDuration}</Text>
                                ) : null}
                                <Text type="secondary">Serves {hydratedRecipe.serves ?? "Unknown"}</Text>
                            </Space>
                            {hydratedRecipe.recipeDescription ? (
                                <Paragraph style={{ margin: "18px 0 0", maxWidth: 680, color: token.colorTextSecondary, fontSize: 16, lineHeight: 1.65 }}>
                                    {hydratedRecipe.recipeDescription}
                                </Paragraph>
                            ) : null}
                            {hydratedRecipe.url ? (
                                <Paragraph style={{ margin: "12px 0 0" }}>
                                    <a href={hydratedRecipe.url} target="_blank" rel="noopener noreferrer">
                                        <LinkOutlined /> View original recipe
                                    </a>
                                </Paragraph>
                            ) : null}
                        </Col>
                        <Col xs={24} lg={9}>
                            <Space
                                data-testid="recipe-detail-tags"
                                wrap
                                size={[8, 8]}
                                style={{ justifyContent: isLg ? "flex-end" : "flex-start", display: "flex" }}
                            >
                                {recipeDetailTags}
                            </Space>
                        </Col>
                    </Row>
                </div>
            </section>

            <Card
                className="shadowed-card"
                style={{ background: sageCreamPalette.cardBg }}
                styles={{ body: { padding: isLg ? "16px" : "8px" } }}
            >
                <Tabs
                    className="scrollable-tabs"
                    defaultActiveKey="instructions"
                    items={[
                        {
                            key: "instructions",
                            label: "Instructions",
                            children: (
                                <RecipeInstructionsSection
                                    steps={instructionSteps}
                                    formatQuantity={formatQuantity}
                                    appearance={{
                                        borderColor: token.colorBorderSecondary,
                                        badgeBackground: token.colorPrimary,
                                        badgeColor: "#fff",
                                    }}
                                />
                            ),
                        },
                        {
                            key: "ingredients",
                            label: "Ingredients",
                            children: (
                                <RecipeIngredientsSection
                                    items={consolidatedIngredients}
                                    formatQuantity={formatQuantity}
                                    appearance={{ borderColor: token.colorBorderSecondary }}
                                />
                            ),
                        },
                        {
                            key: "nutrition",
                            label: "Nutrition",
                            children: (
                                <MacrosTable macros={hydratedRecipe} small={!isLg} context="recipe" />
                            ),
                        },
                    ]}
                />
            </Card>
        </PagePadding>
    );
}
